import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { Reveal, MaskLine, EASE } from "@/lib/motion";
import { SERVICES, PHOTOS, SITE } from "@/data/site";
import { BookingLink } from "@/components/BookingLink";
import { EmblemDivider } from "@/components/EmblemDivider";
import { Packages } from "@/components/Packages";
import { Photo } from "@/components/Photo";

const BTN = "group inline-flex items-center gap-3 min-h-[48px] px-8 py-3.5 font-mono text-[13px] uppercase tracking-[0.14em] transition-all duration-300";

const DETAILS = {
  monthly: ["Recording income and expenses", "Categorizing transactions in QuickBooks", "Keeping your books current each month", "Organizing your financial records"],
  reconciliation: ["Matching bank statements to your books", "Reviewing credit card activity", "Catching missing or duplicate entries", "Keeping your balances accurate"],
  reporting: ["Profit & loss statements", "Balance sheet preparation", "Reports written to be readable", "A clearer view of your numbers"],
};

const WORKFLOW = [
  { num: "01", title: "Discovery Call", desc: "We talk about your business, your current books, and what you're looking for help with — by phone or video, wherever you are in the U.S." },
  { num: "02", title: "Getting Set Up", desc: "We organize your chart of accounts in QuickBooks so your records stay consistent from the start." },
  { num: "03", title: "Monthly Bookkeeping", desc: "Ongoing recording, categorizing, and reconciliation delivered on a dependable schedule." },
  { num: "04", title: "Reports & Summary", desc: "You receive monthly reports and a brief written summary. The Standard package also includes a 30-minute monthly report review call." },
];

const ServicesHero = () => (
  <section data-testid="services-hero" className="bg-navy text-parchment pt-40 pb-24 relative overflow-hidden">
    <div className="max-w-[1300px] mx-auto px-6 lg:px-10">
      <p className="font-mono text-[12px] uppercase tracking-[0.28em] text-gold mb-8">Services</p>
      <h1 className="font-serif font-medium tracking-tight leading-[1.04] text-5xl sm:text-6xl lg:text-7xl">
        <MaskLine delay={0.1}>How we keep</MaskLine>
        <MaskLine delay={0.24}>your books <span className="italic text-gold">right.</span></MaskLine>
      </h1>
      <p className="anim-up mt-8 max-w-2xl text-parchment-muted text-lg leading-relaxed" style={{ animationDelay: "0.5s" }}>
        Oakford Ledger focuses on a few core bookkeeping services, all handled in QuickBooks and
        offered as fixed monthly packages based on your transaction volume and accounts.
        <span data-testid="services-service-area" className="block mt-3 text-parchment">{SITE.serviceAreaShort}</span>
      </p>
    </div>
  </section>
);

// Core services, with the single Services photo beside the explanation (stacked above it on mobile).
const Matrix = () => {
  const [active, setActive] = useState(SERVICES[0].id);
  const current = SERVICES.find((s) => s.id === active);
  return (
    <section data-testid="service-matrix-tabs" className="bg-cream pt-16 pb-28">
      <EmblemDivider tone="light" className="mb-20" />
      <div className="max-w-[1300px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10 lg:gap-14">
        <div className="lg:col-span-5">
          <Reveal>
            <Photo photo={PHOTOS.servicesDesk} testId="services-photo" className="mb-10" />
          </Reveal>
          <p className="font-mono text-[12px] uppercase tracking-[0.28em] text-[#8a6b2f] mb-6">Core services</p>
          <div className="flex flex-col">
            {SERVICES.map((s) => (
              <button key={s.id} data-testid={`tab-${s.id}`} onClick={() => setActive(s.id)} aria-pressed={active === s.id}
                className={`text-left min-h-[44px] py-4 border-t border-[#E2DACB] transition-all duration-300 flex items-baseline gap-4 ${
                  active === s.id ? "pl-3" : "hover:pl-2 opacity-60 hover:opacity-100"
                }`}>
                <span className="font-mono text-xs text-[#8a6b2f]">{s.num}</span>
                <span className={`font-serif text-2xl lg:text-3xl ${active === s.id ? "text-navy" : "text-ink-muted"}`}>{s.title}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="lg:col-span-7 lg:pl-14 lg:border-l border-[#E2DACB]">
          <AnimatePresence mode="wait">
            <motion.div key={active}
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.5, ease: EASE }}>
              <h2 className="font-serif text-4xl lg:text-5xl text-navy tracking-tight leading-tight">{current.title}</h2>
              <p className="text-ink-muted mt-6 text-lg leading-relaxed max-w-2xl">{current.desc}</p>
              <ul className="mt-10 grid sm:grid-cols-2 gap-5">
                {DETAILS[active].map((d) => (
                  <li key={d} className="flex items-start gap-3 border-b border-[#E2DACB] pb-4">
                    <Check size={16} className="text-[#8a6b2f] mt-1 shrink-0" />
                    <span className="text-navy text-[15px]">{d}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};

const Workflow = () => (
  <section data-testid="engagement-workflow-section" className="bg-navy-2 text-parchment pt-16 pb-28 relative">
    <EmblemDivider tone="dark" className="mb-20" />
    <div className="max-w-[1300px] mx-auto px-6 lg:px-10">
      <Reveal>
        <p className="font-mono text-[12px] uppercase tracking-[0.28em] text-gold mb-5">How we work together</p>
        <h2 className="font-serif text-4xl lg:text-5xl tracking-tight max-w-2xl leading-tight">A clear path from first call to monthly rhythm</h2>
      </Reveal>
      <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#24355A]">
        {WORKFLOW.map((w, i) => (
          <Reveal key={w.num} delay={i * 0.07}>
            <div className="bg-navy-2 p-8 h-full hover:bg-navy transition-colors duration-500">
              <span className="font-serif text-5xl text-gold">{w.num}</span>
              <h3 className="font-serif text-2xl mt-6">{w.title}</h3>
              <p className="text-parchment-muted mt-4 leading-relaxed text-[15px]">{w.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const InquiryCTA = () => (
  <section data-testid="services-inquiry-cta" className="bg-cream pt-16 pb-28">
    <EmblemDivider tone="light" className="mb-20" />
    <div className="max-w-[1300px] mx-auto px-6 lg:px-10 text-center">
      <Reveal>
        <p className="font-mono text-[12px] uppercase tracking-[0.28em] text-[#8a6b2f] mb-6">Next step</p>
        <h2 className="font-serif text-4xl lg:text-6xl text-navy tracking-tight max-w-3xl mx-auto leading-[1.06]">
          Not sure which package fits? Let's talk through your books.
        </h2>
        <div className="mt-10 flex flex-wrap gap-4 justify-center">
          <BookingLink placement="services-cta" testId="services-consultation-btn" className={`${BTN} bg-navy text-parchment border border-gold/30 hover:-translate-y-0.5`}>
            Book a Consultation <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
          </BookingLink>
          <Link to="/contact" data-testid="services-inquiry-btn" className={`${BTN} text-navy border border-navy/30 hover:bg-navy/5`}>
            Send an Inquiry
          </Link>
        </div>
      </Reveal>
    </div>
  </section>
);

export default function Services() {
  return (
    <>
      <ServicesHero />
      <Packages />
      <Matrix />
      <Workflow />
      <InquiryCTA />
    </>
  );
}
